import "@fontsource/ibm-plex-sans/latin-400.css";
import "@fontsource/ibm-plex-sans/latin-500.css";
import "@fontsource/ibm-plex-sans/latin-600.css";
import "@fontsource/newsreader/latin-400.css";
import "@fontsource/newsreader/latin-500.css";
import "./styles.css";
import { landing, lending, notFound, offlinePage } from "./public";
import { isStandalone, startPwa } from "./pwa";
import { startTheme, syncTheme } from "./theme";
import { MARK, app, handOverQuery, html, leave, mount, navigate, newerBuild, shown, toast } from "./ui";
import { holdMove } from "./unsaved";

type View = () => void | Promise<void>;

/**
 * Public pages ship in the main bundle. Everything else loads on first use, so a phone that
 * scans the QR code downloads only the self-service screens and never the staff workspace.
 */
const ROUTES: Record<string, () => View | Promise<View>> = {
  "/": () => landing,
  "/lending": () => lending,
  "/self-service": () => import("./self-service-app").then((module) => module.selfService),
  "/staff": () => import("./staff-shell").then((module) => module.staffLogin),
  "/staff/home": () => import("./home-workspace").then((module) => module.homeWorkspace),
  "/staff/items": () => import("./staff").then((module) => module.workspace),
  "/staff/catalogue": () => import("./catalogue-workspace").then((module) => module.catalogueWorkspace),
  "/staff/locations": () => import("./locations-workspace").then((module) => module.locationsWorkspace),
  "/staff/kits": () => import("./kits-workspace").then((module) => module.kitsWorkspace),
  "/staff/stock": () => import("./stock-workspace").then((module) => module.stockWorkspace),
  "/staff/loans": () => import("./loans-workspace").then((module) => module.loansWorkspace),
  "/staff/self-service": () => import("./self-service-review").then((module) => module.selfServiceReview),
  "/staff/attention": () => import("./attention-workspace").then((module) => module.attentionWorkspace),
  "/staff/activity": () => import("./activity-workspace").then((module) => module.activityWorkspace),
  "/staff/admin": () => import("./admin-system").then((module) => module.systemStatus),
  "/staff/admin/self-service": () => import("./admin-self-service").then((module) => module.selfServiceSettings),
  "/staff/admin/catalog": () => import("./admin-catalog").then((module) => module.catalogSettings),
  "/staff/admin/staff": () => import("./admin").then((module) => module.staffAccounts),
  "/staff/admin/accountability": () => import("./admin-accountability").then((module) => module.accountability),
  "/staff/admin/directory": () => import("./directory-workspace").then((module) => module.staffDirectory),
  "/staff/account": () => import("./admin").then((module) => module.myAccount)
};

/**
 * The Logistics Catalog has its own page (staff/catalogue.html), which links its manifest from the first byte; every other route is
 * index.html, which links Self-Service's. Browsers read the manifest as the page loads (iOS only then), so moving between the two apps
 * loads the other page rather than re-pointing the manifest (docs/OFFLINE_CATALOGUE.md).
 */
const CATALOG_PAGE = document.documentElement.dataset.app === "catalog";
const catalogRoute = (path: string) => path === "/staff/catalogue";

const staffPlaceholder = html`<header class="app-bar"><div class="app-bar__inner"><span class="app-bar__brand" aria-hidden="true"><span>${MARK}</span></span></div></header>
  <main id="main-content" class="app-main" aria-busy="true"><div class="page-header"><span class="skeleton skeleton--heading"></span></div><div class="skeleton skeleton--block"></div></main>`;

/** A page's shape while its code and data load: title and lede, a strip of views, and rows. */
const pageSkeleton = html`<header class="page-header" aria-hidden="true"><div class="page-header__title"><span class="skeleton skeleton--heading"></span><span class="skeleton skeleton--text"></span></div></header>
  <div class="views views--loading" aria-hidden="true"><span class="skeleton skeleton--text"></span><span class="skeleton skeleton--text"></span><span class="skeleton skeleton--text"></span></div>
  <div class="data-table-wrap" aria-hidden="true">${Array.from({ length: 8 }, () => html`<div class="skeleton-row"><span class="skeleton skeleton--text"></span><span class="skeleton skeleton--num"></span></div>`)}</div>
  <p class="visually-hidden" role="status">Loading</p>`;

/** A section's shape below a page's title and tabs, which stay (V1.16.5). */
const sectionSkeleton = html`<div class="data-table-wrap" aria-hidden="true">${Array.from({ length: 6 }, () => html`<div class="skeleton-row"><span class="skeleton skeleton--text"></span><span class="skeleton skeleton--num"></span></div>`)}</div>
  <p class="visually-hidden" role="status">Loading</p>`;

/**
 * A move between staff pages (Earl, 2026-10-24): the page being left lets go at once, the rail marks where it is going, and the next page's
 * shape shows while its code and data load, so the switch itself is instant. Nothing of the old page lingers or keeps polling meanwhile.
 */
function showNextPage(path: string): void {
  const main = document.querySelector<HTMLElement>("#main-content.app-main");
  if (!main || !document.querySelector(".app-bar")) return;
  leave();
  for (const link of document.querySelectorAll<HTMLAnchorElement>(".app-nav__link[href]")) {
    if (link.getAttribute("href") === path) link.setAttribute("aria-current", "page"); else link.removeAttribute("aria-current");
  }
  main.setAttribute("aria-busy", "true");
  // Between the tabs of one page (Administration's sections), its title and tabs stay, the chosen tab is marked, and only what is
  // below them waits; the section then draws in place.
  const tabs = main.querySelector<HTMLElement>(":scope > .subnav");
  const tab = tabs ? [...tabs.querySelectorAll<HTMLAnchorElement>("a[href]")].find((link) => link.getAttribute("href") === path) : undefined;
  if (!tabs || !tab) { mount(main, pageSkeleton); return; }
  for (const link of tabs.querySelectorAll("a[href]")) {
    if (link === tab) link.setAttribute("aria-current", "page"); else link.removeAttribute("aria-current");
  }
  while (tabs.nextSibling) tabs.nextSibling.remove();
  // What belongs to the section being left, its lede and its buttons, is hidden where it stands (so nothing moves) and cannot be
  // used until the next section draws its own.
  for (const part of main.querySelectorAll<HTMLElement>(":scope > .page-header p, :scope > .page-header .page-header__actions")) {
    part.inert = true;
    part.style.visibility = "hidden";
  }
  tabs.insertAdjacentHTML("afterend", sectionSkeleton.value);
}

let navigation = 0;
// A fresh page load already starts at the top, so only in-app navigation moves focus to the page body. It never does inside a
// frame (Administration's Self-Service test panel): that would pull focus out of the page around it.
let loaded = false;

async function render(): Promise<void> {
  const current = ++navigation;
  const moveFocus = loaded && window.self === window.top;
  const focusedBefore = document.activeElement;
  const first = !loaded;
  loaded = true;
  shown.address = window.location.pathname + window.location.search;
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  syncTheme(path);
  if (path.startsWith("/staff/") && !ROUTES[path]) return navigate("/staff/home", true);
  // In a browser tab the Catalog only offers its app (LH-IP02-S03), so a place check from an older link (Home's Continue, a bookmark)
  // opens in Locations instead. The installed Catalog keeps running checks itself.
  const check = catalogRoute(path) && !isStandalone() ? new URLSearchParams(window.location.search).get("audit") : null;
  if (check && /^LA-[A-Za-z0-9-]+$/.test(check)) return navigate(`/staff/locations?check=${check}`, true);
  // Offline the server cannot hand over the other page; the router renders what it can (the offline page) instead.
  if (catalogRoute(path) !== CATALOG_PAGE && navigator.onLine && sessionStorage.getItem("reloaded-for-page") !== path) {
    sessionStorage.setItem("reloaded-for-page", path);
    window.location.reload();
    return;
  }
  sessionStorage.removeItem("reloaded-for-page");
  // Opened or refreshed on a staff page: the bar and the page's shape show at once, while its code and the session load, instead of
  // a blank screen (V1.15 perceived performance). The page replaces it whole; in-app moves keep the page they leave until then.
  // A newer release is running (the session answer says so): this move loads the page afresh instead, so the tab picks it up.
  const newer = !first && path.startsWith("/staff/") ? newerBuild() : null;
  if (newer) {
    sessionStorage.setItem("reloaded-for-build", newer);
    window.location.reload();
    return;
  }
  // A cold staff page loads the shell beside its own code; the shell asks who is signed in as soon as it runs (V1.16.4 §4.8).
  if (first && path.startsWith("/staff/") && !catalogRoute(path)) void import("./staff-shell").catch(() => undefined);
  if (first && path.startsWith("/staff/") && !app.childElementCount) mount(app, staffPlaceholder);
  else if (!first && path.startsWith("/staff/") && !catalogRoute(path)) showNextPage(path);
  let view: View;
  try {
    // Staff tools are not cached. Render their offline message before attempting an
    // uncached import; the Catalogue alone has its own offline lease and screens.
    const staffOffline = !navigator.onLine && (path === "/staff" || path.startsWith("/staff/")) && !catalogRoute(path);
    view = staffOffline ? offlinePage : await (ROUTES[path] ?? (() => notFound))();
  } catch {
    // A connected network adapter can still have no route to the server. Confirm
    // reachability before treating a failed import as a replaced deployment.
    let reachable = false;
    if (navigator.onLine) {
      // The public homepage is reachable without a session; build.json is deliberately private.
      try { reachable = (await fetch("/", { cache: "no-store", signal: AbortSignal.timeout(5_000) })).ok; } catch { /* no connection */ }
    }
    if (current !== navigation) return;
    if (reachable) {
      // A deploy replaced the files this page was built with: load the new version once.
      if (!sessionStorage.getItem("reloaded-for-update")) {
        sessionStorage.setItem("reloaded-for-update", "1");
        window.location.reload();
      }
      return;
    }
    view = offlinePage;
  }
  sessionStorage.removeItem("reloaded-for-update");
  // A quicker, later navigation already rendered; this one is stale.
  if (current !== navigation) return;
  leave();
  void view();
  window.scrollTo(0, 0);
  if (!moveFocus) return;
  window.requestAnimationFrame(() => {
    const main = document.querySelector<HTMLElement>("#main-content");
    // A view that already put focus in its page on purpose (the Catalogue's Name field) keeps it.
    const focused = document.activeElement;
    if (main && focused && focused !== focusedBefore && focused !== main && main.contains(focused)) return;
    main?.setAttribute("tabindex", "-1");
    main?.focus({ preventScroll: true });
  });
}

// One delegated listener turns same-origin data-route links into client-side navigation.
document.addEventListener("click", (event) => {
  const link = (event.target as HTMLElement).closest<HTMLAnchorElement>("a[data-route]");
  if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target) return;
  event.preventDefault();
  if (link.pathname !== window.location.pathname || link.search !== window.location.search) navigate(link.pathname + link.search);
});

// Pointing at, touching or tabbing to a link starts loading its page's code, so the switch itself rarely waits on the network.
const warm = (event: Event) => {
  const link = (event.target as Element).closest?.<HTMLAnchorElement>("a[data-route]");
  const load = link && link.origin === window.location.origin ? ROUTES[link.pathname.replace(/\/+$/, "") || "/"] : undefined;
  if (load) void Promise.resolve().then(load).catch(() => { /* the real navigation reports failures */ });
};
document.addEventListener("pointerover", warm);
document.addEventListener("focusin", warm);

// "/" jumps to the page's search field, as in most catalog and admin tools.
document.addEventListener("keydown", (event) => {
  if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
  const target = event.target as HTMLElement;
  if (target.closest("input, textarea, select, [contenteditable=true]") || document.querySelector("dialog[open]")) return;
  const search = document.querySelector<HTMLInputElement>("[data-search]");
  if (!search) return;
  event.preventDefault();
  search.focus();
  search.select();
});

window.addEventListener("unhandledrejection", (event) => {
  console.error(event.reason);
  toast("Something went wrong. Please try again.", "error");
});

// A same-page #fragment jump, or an overlay's own history entry (the photo viewer), also fires popstate; only a new path or query is a new view.
window.addEventListener("popstate", () => {
  // Back or Forward while something is unsaved is put back and asked about first (unsaved.ts).
  if (holdMove()) return;
  if (window.location.pathname + window.location.search === shown.address) return;
  if (window.location.pathname === new URL(shown.address, window.location.href).pathname && handOverQuery()) {
    shown.address = window.location.pathname + window.location.search;
    return;
  }
  render();
});
startTheme();
startPwa();
void render();
